import React, { useEffect, useState } from 'react'
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { Toast } from '../../stores/toastStore'

interface ToastComponentProps {
  toast: Toast
  onRemove: (id: string) => void
}

export const ToastComponent: React.FC<ToastComponentProps> = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isLeaving, setIsLeaving] = useState(false)

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  const handleRemove = () => {
    setIsLeaving(true)
    setTimeout(() => onRemove(toast.id), 300)
  }

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5" />
      case 'error':
        return <AlertCircle className="h-5 w-5" />
      case 'warning':
        return <AlertTriangle className="h-5 w-5" />
      case 'info':
        return <Info className="h-5 w-5" />
      default:
        return <Info className="h-5 w-5" />
    }
  }

  return (
    <div
      className={cn(
        // Base styles
        "relative flex w-full max-w-sm items-start gap-3 rounded-lg border p-4 shadow-lg",
        "transform transition-all duration-300 ease-out",
        
        // Animation states
        isVisible && !isLeaving && "translate-x-0 opacity-100",
        !isVisible && "translate-x-full opacity-0",
        isLeaving && "translate-x-full opacity-0 scale-95",
        
        // Type-specific styling
        {
          'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200': 
            toast.type === 'success',
          'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200': 
            toast.type === 'error',
          'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-200': 
            toast.type === 'warning',
          'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200': 
            toast.type === 'info',
        }
      )}
    >
      {/* Icon */}
      <div className={cn(
        "flex-shrink-0",
        {
          'text-green-600 dark:text-green-400': toast.type === 'success',
          'text-red-600 dark:text-red-400': toast.type === 'error',
          'text-yellow-600 dark:text-yellow-400': toast.type === 'warning',
          'text-blue-600 dark:text-blue-400': toast.type === 'info',
        }
      )}>
        {getIcon()}
      </div>

      {/* Content */}
      <div className="flex-1 space-y-1">
        <div className="font-semibold text-sm">
          {toast.title}
        </div>
        {toast.description && (
          <div className="text-sm opacity-90 leading-relaxed">
            {toast.description}
          </div>
        )}
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            className={cn(
              "text-sm font-medium underline underline-offset-2 hover:no-underline transition-all",
              {
                'text-green-700 hover:text-green-800 dark:text-green-300 dark:hover:text-green-200': 
                  toast.type === 'success',
                'text-red-700 hover:text-red-800 dark:text-red-300 dark:hover:text-red-200': 
                  toast.type === 'error',
                'text-yellow-700 hover:text-yellow-800 dark:text-yellow-300 dark:hover:text-yellow-200': 
                  toast.type === 'warning',
                'text-blue-700 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-200': 
                  toast.type === 'info',
              }
            )}
          >
            {toast.action.label}
          </button>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={handleRemove}
        className={cn(
          "flex-shrink-0 rounded-full p-1 transition-colors hover:bg-black/10 dark:hover:bg-white/10",
          "focus:outline-none focus:ring-2 focus:ring-offset-2",
          {
            'focus:ring-green-500': toast.type === 'success',
            'focus:ring-red-500': toast.type === 'error',
            'focus:ring-yellow-500': toast.type === 'warning',
            'focus:ring-blue-500': toast.type === 'info',
          }
        )}
      >
        <X className="h-4 w-4" />
      </button>

      {/* Progress bar for duration */}
      {toast.duration && toast.duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 dark:bg-white/10 rounded-b-lg overflow-hidden">
          <div 
            className={cn(
              "h-full transition-all ease-linear",
              {
                'bg-green-600': toast.type === 'success',
                'bg-red-600': toast.type === 'error',
                'bg-yellow-600': toast.type === 'warning',
                'bg-blue-600': toast.type === 'info',
              }
            )}
            style={{
              width: '100%',
              animation: `shrink ${toast.duration}ms linear forwards`,
            }}
          />
        </div>
      )}

      <style jsx>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  )
}